import React, { useEffect, useMemo, useState } from 'react';
import { Box, Chip, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import AddReactionIcon from '@mui/icons-material/AddReaction';
import { diskussionService } from '../../services/diskussionService';
import type { DiskussionReactionSummary } from '../../types';

export const ALLOWED_REACTION_EMOJIS = ['👍', '👎', '❤️', '😂', '🎉', '🤔', '👀', '✅'] as const;

export interface DiskussionPostReactionsProps {
  postId: number;
  reactions: DiskussionReactionSummary[];
  disabled?: boolean;
  onReactionsChange: (reactions: DiskussionReactionSummary[]) => void;
}

function formatReactorsTooltip(reaction: DiskussionReactionSummary): string {
  if (reaction.reactors.length === 0) return reaction.emoji;
  return reaction.reactors.map((r) => r.name).join('\n');
}

const DiskussionPostReactions: React.FC<DiskussionPostReactionsProps> = ({
  postId,
  reactions,
  disabled = false,
  onReactionsChange,
}) => {
  const [localReactions, setLocalReactions] = useState(reactions);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setLocalReactions(reactions);
  }, [reactions]);

  const myReaction = useMemo(
    () => localReactions.find((r) => r.reacted_by_me),
    [localReactions],
  );

  const handleToggle = async (emoji: string, reactedByMe: boolean) => {
    if (disabled || busy) return;
    setBusy(true);
    const prev = localReactions;
    try {
      const next = reactedByMe
        ? await diskussionService.removeReaction(postId)
        : await diskussionService.setReaction(postId, emoji);
      setLocalReactions(next);
      onReactionsChange(next);
    } catch {
      setLocalReactions(prev);
    } finally {
      setBusy(false);
    }
  };

  const handlePickEmoji = async (emoji: string) => {
    setMenuAnchor(null);
    if (disabled || busy) return;
    if (myReaction?.emoji === emoji) {
      await handleToggle(emoji, true);
      return;
    }
    setBusy(true);
    const prev = localReactions;
    try {
      const next = await diskussionService.setReaction(postId, emoji);
      setLocalReactions(next);
      onReactionsChange(next);
    } catch {
      setLocalReactions(prev);
    } finally {
      setBusy(false);
    }
  };

  const visibleEmojis = useMemo(() => {
    const fromReactions = localReactions.map((r) => r.emoji);
    const extras = ALLOWED_REACTION_EMOJIS.filter((e) => !fromReactions.includes(e));
    return [...fromReactions, ...extras];
  }, [localReactions]);

  if (disabled && localReactions.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
      {localReactions.map((r) => (
        <Tooltip key={r.emoji} title={formatReactorsTooltip(r)}>
          <Chip
            size="small"
            label={`${r.emoji} ${r.count}`}
            variant={r.reacted_by_me ? 'filled' : 'outlined'}
            color={r.reacted_by_me ? 'primary' : 'default'}
            onClick={disabled ? undefined : () => handleToggle(r.emoji, r.reacted_by_me)}
            sx={{ cursor: disabled ? 'default' : 'pointer' }}
          />
        </Tooltip>
      ))}
      {!disabled && (
        <>
          <Tooltip title="Reaction hinzufügen">
            <IconButton
              size="small"
              aria-label="Reaction hinzufügen"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              disabled={busy}
            >
              <AddReactionIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
          >
            {visibleEmojis.map((emoji) => (
              <MenuItem
                key={emoji}
                onClick={() => handlePickEmoji(emoji)}
                selected={myReaction?.emoji === emoji}
              >
                {emoji}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Box>
  );
};

export default DiskussionPostReactions;
